import type {
  DeviceFilter,
  PinPageGroup,
  SidebarPinEntry,
  SortOrder,
} from './types';
import type { DevicePreset, Pin } from '@/types/feedback';

/** Pins saved before device presets existed are counted as desktop. */
function pinDevice(pin: Pin): DevicePreset {
  return pin.devicePreset === 'mobile' ? 'mobile' : 'desktop';
}

export function resolveDeviceFilter(
  allDevices: boolean,
  device: DevicePreset,
): DeviceFilter {
  if (allDevices) return 'all';
  return device === 'mobile' || device === 'desktop' ? device : 'all';
}

export function isPinResolved(pin: Pin): boolean {
  return pin.status === 'RESOLVED';
}

/** Unresolved pins across every device — same total the toolbar heading uses. */
export function countOpenPins(pins: Pin[]): number {
  return pins.filter((pin) => !isPinResolved(pin)).length;
}

/** Counts exclude resolved pins so the tabs mirror the open-comment heading. */
export function countPinsByDevice(pins: Pin[]): Record<DeviceFilter, number> {
  const openPins = pins.filter((pin) => !isPinResolved(pin));
  const mobile = openPins.filter((pin) => pinDevice(pin) === 'mobile').length;
  return { all: openPins.length, mobile, desktop: openPins.length - mobile };
}

/**
 * Numbers every pin by its position in the unfiltered list, then narrows to the
 * selected device and sorts. Numbering happens first so a pin keeps the same
 * badge number regardless of the active filter.
 */
export function buildPinEntries(
  pins: Pin[],
  deviceFilter: DeviceFilter,
  sortOrder: SortOrder,
): SidebarPinEntry[] {
  return pins
    .map((pin, index) => ({ pin, pinNumber: index + 1 }))
    .filter(
      ({ pin }) => deviceFilter === 'all' || pinDevice(pin) === deviceFilter,
    )
    .sort((a, b) =>
      sortOrder === 'newest'
        ? b.pin.createdAt - a.pin.createdAt
        : a.pin.createdAt - b.pin.createdAt,
    );
}

export function groupPinsByPage(entries: SidebarPinEntry[]): PinPageGroup[] {
  const groups = new Map<string, SidebarPinEntry[]>();

  for (const entry of entries) {
    const path = entry.pin.pagePath || '/';
    const group = groups.get(path);
    if (group) {
      group.push(entry);
    } else {
      groups.set(path, [entry]);
    }
  }

  return Array.from(groups.entries());
}

function normalizePath(path: string): string {
  return path.replace(/^\/+|\/+$/g, '');
}

export function isHomePagePath(path: string, targetUrl?: string): boolean {
  let homePagePath = '';
  if (targetUrl) {
    try {
      homePagePath = normalizePath(new URL(targetUrl).pathname);
    } catch {
      // An invalid target URL should not prevent the sidebar from rendering.
    }
  }

  const pagePath = normalizePath(path);
  return !pagePath || pagePath === homePagePath;
}

export function formatPagePathLabel(path: string): string {
  const segment = path.split('/').filter(Boolean).pop() ?? path;
  return segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
